import {
  useQuery,
  useMutation,
  useQueryClient,
  useInfiniteQuery,
  UseQueryOptions,
  UseMutationOptions,
  UseInfiniteQueryOptions,
} from 'react-query'
import { MembershipRatesApi } from '@trybeapp/sdk.customer'
import {
  ListMembershipRates,
  ListMembershipRatesRequest,
  MembershipRate,
  MembershipRatesApi as MembershipRatesApiTs,
  MembershipType,
  ResponseError,
} from '@trybeapp/sdk'
import { apiConfiguration } from 'utilities/TrybeApiClient'
import { ApiResponse, LegacySdkResponseError } from 'api/api.types'

const legacyApi = new MembershipRatesApi()
const api = new MembershipRatesApiTs(apiConfiguration)

export const useMembershipRates = (
  params: ListMembershipRatesRequest,
  options: UseQueryOptions<ListMembershipRates, ResponseError> = {}
) => {
  return useQuery<ListMembershipRates, ResponseError>(
    ['membershipRates', { params }],
    () => api.listMembershipRates(params),
    options
  )
}

export const useMembershipRate = (
  membershipRateId: MembershipRate['id'],
  options: UseQueryOptions<MembershipRate, LegacySdkResponseError> = {}
) => {
  return useQuery<MembershipRate, LegacySdkResponseError>(
    ['membershipRate', membershipRateId],
    async () => (await legacyApi.getMembershipRate(membershipRateId)).data,
    options
  )
}

interface ListMembershipRatesData {
  data: MembershipRate[]
  nextPage: number
  page: number | undefined
  total: number
}

export const useInfiniteMembershipRates = (
  params: ListMembershipRatesRequest = {},
  options: UseInfiniteQueryOptions<ListMembershipRatesData, ResponseError> = {}
) =>
  useInfiniteQuery<ListMembershipRatesData, ResponseError>(
    ['membershipRates', { params }],
    async ({ pageParam: page = 1 }) => {
      const response = await api.listMembershipRates({ page, ...params })

      return {
        data: response.data,
        page,
        nextPage: response.meta.last_page > page ? page + 1 : undefined,
        total: response.meta.total,
      }
    },
    {
      getNextPageParam: (lastPage) => lastPage.nextPage,
      ...options,
    }
  )

export const useCreateMembershipRate = (
  membershipTypeId: MembershipType['id'],
  options: UseMutationOptions<
    ApiResponse<MembershipRate>,
    LegacySdkResponseError,
    Record<string, any>
  > = {}
) => {
  const queryClient = useQueryClient()

  return useMutation<ApiResponse<MembershipRate>, LegacySdkResponseError, Record<string, any>>(
    (membershipRate) =>
      legacyApi.createMembershipRate({
        ...membershipRate,
        membership_type_id: membershipTypeId,
      }),
    {
      ...options,
      onSuccess: (...args) => {
        queryClient.invalidateQueries('membershipRates')
        options.onSuccess?.(...args)
      },
    }
  )
}

export const useUpdateMembershipRate = (
  membershipRateId: MembershipRate['id'],
  options: UseMutationOptions<
    ApiResponse<MembershipRate>,
    LegacySdkResponseError,
    Record<string, any>
  > = {}
) => {
  const queryClient = useQueryClient()

  return useMutation<ApiResponse<MembershipRate>, LegacySdkResponseError, Record<string, any>>(
    (membershipRate) => legacyApi.updateMembershipRate(membershipRateId, membershipRate),
    {
      ...options,
      onSuccess: (...args) => {
        queryClient.invalidateQueries('membershipRates')
        queryClient.invalidateQueries(['membershipRate', membershipRateId])
        options.onSuccess?.(...args)
      },
    }
  )
}

export const useDeleteMembershipRate = (
  options: UseMutationOptions<void, LegacySdkResponseError, MembershipRate['id']> = {}
) => {
  const queryClient = useQueryClient()

  return useMutation<void, LegacySdkResponseError, MembershipRate['id']>(
    (membershipRateId) => legacyApi.archiveMembershipRate(membershipRateId),
    {
      ...options,
      onSuccess: (...args) => {
        const [, membershipRateId] = args

        queryClient.invalidateQueries('membershipRates')
        queryClient.invalidateQueries(['membershipRate', membershipRateId])
        options.onSuccess?.(...args)
      },
    }
  )
}
